import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import groupService from '../services/groupService';
import '../styles/group-discovery.css';

const GroupDiscovery = ({ onGroupJoined }) => {
  const navigate = useNavigate();
  const [publicGroups, setPublicGroups] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('browse'); // 'browse' or 'search'

  // Load public groups on component mount
  useEffect(() => {
    loadPublicGroups();
  }, []);

  const loadPublicGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Loading public groups...');
      const response = await groupService.getPublicGroups();
      
      if (response.success) {
        setPublicGroups(response.data || []);
        console.log('✅ Public groups loaded:', response.data);
      } else {
        throw new Error(response.error || 'Failed to load public groups');
      }
    } catch (error) {
      console.error('❌ Error loading public groups:', error);
      setError(error.message);
      // For now, show some sample data if backend isn't ready
      setPublicGroups([
        {
          _id: 'sample1',
          name: 'Young Investors Club',
          description: 'A group for learning investment basics',
          memberCount: 15,
          uniqueNumber: 'YIC001',
          isPublic: true,
          category: 'Education'
        },
        {
          _id: 'sample2',
          name: 'Family Portfolio Challenge',
          description: 'Family investment competition',
          memberCount: 8,
          uniqueNumber: 'FAM123',
          isPublic: true,
          category: 'Family'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      console.log('🔍 Searching groups:', query);
      const response = await groupService.searchGroups(query);
      
      if (response.success) {
        setSearchResults(response.data || []);
        console.log('✅ Search results:', response.data);
      } else {
        throw new Error(response.error || 'Failed to search groups');
      }
    } catch (error) {
      console.error('❌ Error searching groups:', error);
      setError(error.message);
      // Show filtered sample data for demo
      const filtered = publicGroups.filter(group =>
        group.name.toLowerCase().includes(query.toLowerCase()) ||
        group.description.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(filtered);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleJoinGroup = async (group) => {
    try {
      console.log('🚪 Joining group:', group.name);
      const response = await groupService.joinGroup(group.uniqueNumber);
      
      if (response.success) {
        console.log('✅ Successfully joined group');
        if (onGroupJoined) {
          onGroupJoined(group);
        }
        // Navigate to the joined group
        navigate(`/groups/${group._id}`);
      } else {
        throw new Error(response.error || 'Failed to join group');
      }
    } catch (error) {
      console.error('❌ Error joining group:', error);
      alert(`Failed to join group: ${error.message}`);
    }
  };

  const renderGroupCard = (group) => (
    <div key={group._id || group.uniqueNumber} className="discovery-group-card">
      <div className="group-card-header">
        <h3>{group.name}</h3>
        <div className="group-badges">
          {group.category && (
            <span className="category-badge">{group.category}</span>
          )}
          {group.isPublic && (
            <span className="visibility-badge">Public</span>
          )}
        </div>
      </div>
      
      <div className="group-card-content">
        <p className="group-description">{group.description}</p>
        <div className="group-stats">
          <span className="member-count">👥 {group.memberCount} members</span>
          <span className="group-code">Code: {group.uniqueNumber}</span>
        </div>
      </div>
      
      <div className="group-card-actions">
        <button 
          onClick={() => handleJoinGroup(group)}
          className="join-btn"
        >
          Join Group
        </button>
        <button 
          onClick={() => navigate(`/groups/${group._id}/preview`)}
          className="preview-btn"
        >
          Preview
        </button>
      </div>
    </div>
  );

  return (
    <div className="group-discovery">
      <div className="discovery-header">
        <h2>🔍 Discover Groups</h2>
        <p>Find and join public investment groups</p>
      </div>

      {/* Tab Navigation */}
      <div className="discovery-tabs">
        <button 
          className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          📋 Browse Public Groups
        </button>
        <button 
          className={`tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 Search Groups
        </button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="search-section">
          <div className="search-input-container">
            <input
              type="text"
              placeholder="Search by group name or description..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              className="search-input"
            />
            <div className="search-icon">🔍</div>
          </div>
          
          {searchLoading && (
            <div className="loading-container">
              <div className="loading-spinner small" />
              <p>Searching groups...</p>
            </div>
          )}
          
          <div className="search-results">
            {searchResults.length > 0 ? (
              <div className="groups-grid">
                {searchResults.map(renderGroupCard)}
              </div>
            ) : searchQuery && !searchLoading ? (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No groups found</h3>
                <p>Try different keywords or browse public groups</p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Browse Tab */}
      {activeTab === 'browse' && (
        <div className="browse-section">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Loading public groups...</p>
            </div>
          ) : error && publicGroups.length === 0 ? (
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <h3>Unable to load groups</h3>
              <p>{error}</p>
              <button onClick={loadPublicGroups} className="retry-btn">
                Try Again
              </button>
            </div>
          ) : (
            <div className="groups-grid">
              {publicGroups.length > 0 ? (
                publicGroups.map(renderGroupCard)
              ) : (
                <div className="no-groups">
                  <div className="no-groups-icon">👥</div>
                  <h3>No public groups yet</h3>
                  <p>Be the first to create a public group!</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Info Section */}
      <div className="discovery-info">
        <h3>💡 Tips for Finding Groups</h3>
        <ul>
          <li>Browse public groups to see what's available</li>
          <li>Use search to find groups by name or topic</li>
          <li>Preview groups before joining to see if they're a good fit</li>
          <li>Create your own public group to attract like-minded investors</li>
        </ul>
      </div>
    </div>
  );
};

export default GroupDiscovery;